import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'

/** A trigger and its panel: the elements one popover answers for. */
type Panels = ReadonlyArray<RefObject<Element | null>>

/**
 * A POPOVER RAISED INSIDE ANOTHER POPOVER BELONGS TO IT.
 *
 * Every floating panel here is portaled to `<body>`, so a menu opened by a
 * control inside a card is that card's CHILD in the React tree and its SIBLING
 * in the document. Both questions dismissal asks — did the press land inside
 * me, did focus leave me — are asked of the DOM through `contains`, and the DOM
 * answers "outside" for the panel the card itself just raised. The card then
 * closes, and closing it unmounts the very menu the reader was reaching for.
 * On the navigation rail that was the whole of printing a document: the
 * language choice could not be reached at all.
 *
 * The containing popover cannot be asked to know what might be raised inside
 * it — a card holds a route's block, and the block holds whatever controls that
 * route draws. So the RAISED panel announces itself instead, and it announces
 * itself from here rather than at each call site: every popover in `web/`
 * already calls `usePopoverDismissal`, so registering there adopts every nested
 * panel that exists today and every one added tomorrow, with nothing to
 * remember at the raiser.
 *
 * Null outside any popover, which is the ordinary case: a menu on a page owes
 * its containment to no one.
 */
const PopoverScopeContext = createContext<((panels: Panels) => () => void) | null>(null)

/**
 * Offered by a popover that may hold controls raising popovers of their own,
 * around its own children. The value is what `usePopoverAdoptions` hands back.
 */
export const PopoverScopeProvider = PopoverScopeContext.Provider

/**
 * The containing popover's half: the panels currently raised inside it, to be
 * counted as ITS OWN for dismissal and for focus-out, and the registration to
 * provide to its children.
 *
 * `adopted` is state rather than a ref because both owners above read their
 * `inside` list through the latest render: a panel adopted without a render
 * would be adopted where nothing can see it.
 */
export function usePopoverAdoptions(): {
  adopted: Panels
  adopt: (panels: Panels) => () => void
} {
  const [raised, setRaised] = useState<ReadonlyArray<Panels>>([])
  const adopt = useCallback((panels: Panels) => {
    setRaised((current) => [...current, panels])
    return () => setRaised((current) => current.filter((entry) => entry !== panels))
  }, [])
  const adopted = useMemo(() => raised.flat(), [raised])
  return { adopted, adopt }
}

/**
 * The dismissal every portaled popover shares: a pointer pressed outside the
 * trigger and the panel closes it. Eight components carried this effect as a
 * private copy — same open guard, same containment walk, same teardown — and a
 * fix to any part of it had to be discovered and applied eight times. This is
 * the one owner.
 *
 * `dismiss` closes WITHOUT moving focus. The outside press is claiming the
 * pointer for whatever it landed on, and yanking focus back to the trigger
 * would fight the very interaction that closed the panel.
 *
 * `escape` is optional because the popovers genuinely differ there. One that
 * moves focus into its panel on opening hears Escape through the panel's own
 * `onKeyDown` and must not also listen on the document — the two would
 * double-fire. One that leaves focus on the trigger has no panel listener to
 * hear the key, so the document is the only ear it has; such a popover passes
 * `escape`, which closes AND returns focus to where typing resumes.
 *
 * The callbacks and the ref list are read through a ref so the listeners are
 * attached once per opening, as every one of the eight originals did, rather
 * than once per render.
 */
export function usePopoverDismissal({
  open,
  inside,
  dismiss,
  escape,
}: {
  open: boolean
  /** The trigger and the panel: a press inside either is the popover's own. */
  inside: ReadonlyArray<RefObject<Element | null>>
  dismiss: () => void
  escape?: () => void
}): void {
  const latest = useRef({ inside, dismiss, escape })
  latest.current = { inside, dismiss, escape }
  const listensForEscape = escape !== undefined

  // Announce this panel to the popover it was raised from, while it stands.
  // The array captured here is the one the cleanup releases, so a panel that
  // re-renders under a standing adoption is not adopted twice.
  const adopt = useContext(PopoverScopeContext)
  useEffect(() => {
    if (!open || adopt === null) {
      return undefined
    }
    return adopt(latest.current.inside)
  }, [open, adopt])

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (latest.current.inside.some((ref) => ref.current?.contains(target))) {
        return
      }
      latest.current.dismiss()
    }
    document.addEventListener('pointerdown', onPointerDown)
    if (!listensForEscape) {
      return () => document.removeEventListener('pointerdown', onPointerDown)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        latest.current.escape?.()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, listensForEscape])
}

/**
 * The other half of dismissal, for a popover a reader can TAB out of: keyboard
 * focus that lands somewhere else closes it. Attach the returned handler as the
 * panel's `onBlur`.
 *
 * IT IGNORES `relatedTarget` ON PURPOSE, which is why it takes no event. A
 * blur's `relatedTarget` names the element about to receive focus only where
 * the engine has decided to move focus by the time the blur is dispatched.
 * Pressing a `<button>` does not focus it in Safari, or in Firefox on macOS,
 * so an option press blurs the search box with a null `relatedTarget`; a
 * handler that read the null as "focus left the popover" closed the panel and
 * unmounted the option before its own click could fire. The tick was lost and
 * the panel vanished. Chromium focuses the button and hands over a
 * `relatedTarget` inside the panel, which is why that code passes review and
 * passes in tests.
 *
 * So it asks `document.activeElement` after a microtask, once the engine has
 * settled wherever it was going. Focus that went NOWHERE — to `body`, or to
 * nothing at all — is not focus that left: the popover stays, and the outside
 * press is dismissed by `usePopoverDismissal` above, which is the owner of
 * that question and reads the pointer rather than guessing at it.
 */
export function usePopoverFocusOut({
  inside,
  dismiss,
}: {
  /** The trigger and the panel: focus inside either is still the popover's. */
  inside: ReadonlyArray<RefObject<Element | null>>
  dismiss: () => void
}): () => void {
  const latest = useRef({ inside, dismiss })
  latest.current = { inside, dismiss }

  return useCallback(() => {
    queueMicrotask(() => {
      const focused = document.activeElement
      if (focused === null || focused === document.body) {
        return
      }
      if (latest.current.inside.some((ref) => ref.current?.contains(focused))) {
        return
      }
      latest.current.dismiss()
    })
  }, [])
}
